/**
 * /billing/school — SCHOOL_ADMIN AR ledger (Plan 14).
 *
 * Renders inline from BillingPage (the parent supplies the branching
 * switcher + the page title); this module owns the AR strip + invoice
 * table + detail drawer.
 *
 * Multi-school rollup behavior (scope item 6 + acceptance #3 / #4):
 *   - permittedSchools.length === 1 → no filter strip; the School
 *     column is hidden in the table; the header reads the org name.
 *   - permittedSchools.length  >  1 → page defaults to "All schools";
 *     filter-chip strip renders above the cards. Selecting a school
 *     narrows the table + re-aggregates the aging totals client-side
 *     (we use the rows we already fetched rather than re-firing the
 *     summary query, so the chip interaction is instant).
 *
 * The bucket card composes with the school filter (acceptance #5) —
 * clicking a card filters the table to that bucket; clicking the same
 * card again clears the filter.
 *
 * Billing-mode fork: a PREPAID (balance load-up) org renders the Balance
 * view instead — ONE org-level balance, no campus chips, no tabs. The
 * Menu prices tab was removed from this page entirely (Sheila, 2026-08-20).
 *
 * Loading state is the block-for-block `BillingSchoolSkeleton`. Pages
 * never render a "Loading…" string per CLAUDE.md.
 */

import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { toast } from '@ordo/ui/sonner'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'

import {
  type ArAgingBucket,
  invoiceManifestCsvUrl,
  invoiceSummaryPdfUrl,
  type SchoolARInvoiceRow,
  useSchoolARSummaryQuery,
} from '@/api/billing-school'
import { useSchoolBillingAccountQuery } from '@/api/billing-school-account'
import { authHeaders, saveBlob } from '@/api/http'
import { ArAgingCards } from '@/components/billing/ar-aging-cards'
import { InstitutionalInvoiceDrawer } from '@/components/billing/institutional-invoice-drawer'
import { InstitutionalInvoiceTable } from '@/components/billing/institutional-invoice-table'
import { OrderReconciliationCard } from '@/components/billing/order-reconciliation-card'
import { ReturnToOrderBar } from '@/components/billing/return-to-order-bar'
import {
  applyTableFilters,
  computeAgingFromRows,
  formatCents,
  invoiceHeadingScope,
  schoolBillingView,
  totalOutstandingCents,
} from '@/components/billing/school-ar-helpers'
import { SchoolBalanceSection } from '@/components/billing/school-balance-section'
import { SchoolFilterChips } from '@/components/billing/school-filter-chips'
import { fetchAuthedCsv, fetchAuthedPdf } from '@/lib/authed-pdf-download'
import { RETURN_TO_PARAM, safeReturnTo } from '@/lib/return-to'
import { BillingSchoolSkeleton } from '@/skeletons/billing-school-skeleton'

/**
 * Download the summary PDF and hand the school the finished file.
 *
 * The route renders in the printing-press service and answers the bytes
 * directly, legacy filename in `content-disposition`; while a render is
 * still running it answers 202 JSON, which is a Retry, not a file. Progress
 * is a toast rather than a blocking dialog — the row stays interactive, and
 * a failure offers Retry instead of a dead end. Shared by the table's row
 * menu and the drawer footer via props.
 */
async function downloadSummaryPdf(row: SchoolARInvoiceRow): Promise<void> {
  const toastId = toast.loading('Preparing the invoice summary…')
  const result = await fetchAuthedPdf(invoiceSummaryPdfUrl(row.id), {
    fallbackFilename: 'invoice-summary.pdf',
    headers: authHeaders(),
  })
  if (result.ok) {
    toast.success('Invoice summary ready', { id: toastId })
    saveBlob(result.blob, result.filename)
    return
  }
  toast.error("Couldn't build the invoice summary", {
    id: toastId,
    description: result.message,
    action: { label: 'Retry', onClick: () => void downloadSummaryPdf(row) },
  })
}

/**
 * Download the item manifest CSV the same way: a bare navigation would drop
 * the impersonation headers and paint a 409/404 JSON body over the app.
 * An 8,000-row district manifest is ~1 MB — buffering it is fine.
 */
async function downloadManifestCsv(row: SchoolARInvoiceRow): Promise<void> {
  const toastId = toast.loading('Preparing the item manifest…')
  const result = await fetchAuthedCsv(invoiceManifestCsvUrl(row.id), {
    fallbackFilename: 'item-manifest.csv',
    headers: authHeaders(),
  })
  if (result.ok) {
    toast.success('Item manifest ready', { id: toastId })
    saveBlob(result.blob, result.filename)
    return
  }
  toast.error("Couldn't build the item manifest", {
    id: toastId,
    description: result.message,
    action: { label: 'Retry', onClick: () => void downloadManifestCsv(row) },
  })
}

export function SchoolARPage() {
  const { data, isPending, error, refetch } = useSchoolARSummaryQuery({ schoolLocationIds: null })

  // launch-gap 09 — school order surfaces link here with ?returnTo=
  // after an insufficient-funds submit; the bar takes the admin back.
  const [searchParams] = useSearchParams()
  const returnTo = safeReturnTo(searchParams.get(RETURN_TO_PARAM))

  // Local UI state: school filter + bucket filter + open drawer.
  const [activeSchoolLocationId, setActiveSchoolLocationId] = useState<string | null>(null)
  const [activeBucket, setActiveBucket] = useState<ArAgingBucket | null>(null)
  const [openInvoice, setOpenInvoice] = useState<SchoolARInvoiceRow | null>(null)

  useEffect(() => {
    if (error) {
      toast.error("Couldn't load School AR", {
        description: error.message,
        action: {
          label: 'Retry',
          onClick: () => {
            void refetch()
          },
        },
      })
    }
  }, [error, refetch])

  const permittedSchools = data?.permittedSchools ?? []
  const isOrgLevel = permittedSchools.length > 1

  // Billing-mode fork (school Billing tab): resolve the ORGANIZATION
  // account via the FIRST permitted school — deliberately NOT chip-driven.
  // Balance load-up is ONE balance for the whole org at this time, so the
  // Balance view never splits by campus. PREPAID renders the Balance view,
  // INVOICED (or unset / unresolved) keeps the invoice AR view. The same
  // query key the checkout gate + top-up watcher use, so the cache is shared.
  const billingAccountSchoolId = permittedSchools[0]?.locationId ?? null
  const accountQuery = useSchoolBillingAccountQuery(billingAccountSchoolId)
  const billingView = schoolBillingView(accountQuery.data ?? null)
  // Stable invoice array reference for useMemo dependency tracking — the
  // raw fallback (`data?.invoices ?? []`) creates a fresh empty array
  // on every render, defeating downstream memoization.
  const invoices = useMemo<SchoolARInvoiceRow[]>(() => data?.invoices ?? [], [data?.invoices])

  // Re-aggregate aging client-side when a single-school chip is
  // selected (so the chip click feels instant + we don't refire the
  // summary query). The aging totals for "All schools" are taken
  // straight from the server payload.
  const visibleRows = useMemo(
    () =>
      applyTableFilters(invoices, {
        schoolLocationIds: activeSchoolLocationId ? [activeSchoolLocationId] : null,
        bucket: activeBucket,
      }),
    [invoices, activeSchoolLocationId, activeBucket],
  )

  const rowsForAging = useMemo(
    () =>
      activeSchoolLocationId
        ? applyTableFilters(invoices, {
            schoolLocationIds: [activeSchoolLocationId],
            bucket: null,
          })
        : invoices,
    [invoices, activeSchoolLocationId],
  )

  const aging = useMemo(() => {
    if (data && !activeSchoolLocationId) {
      return data.aging
    }
    return computeAgingFromRows(rowsForAging)
  }, [data, activeSchoolLocationId, rowsForAging])

  // Hold the skeleton until the billing-mode fork is resolvable too —
  // otherwise a PREPAID school flashes the invoice layout for a beat
  // before the Balance view swaps in. (An account-read error falls
  // through: `isPending` goes false and the safe 'invoices' default renders.)
  if ((isPending && !data) || (permittedSchools.length > 0 && accountQuery.isPending)) {
    return <BillingSchoolSkeleton />
  }

  if (permittedSchools.length === 0) {
    return <NoPermittedSchoolsState />
  }

  // One balance for the whole org (balance load-up): no campus chips, no
  // tabs — the balance section is the page.
  if (billingView === 'balance' && billingAccountSchoolId) {
    const orgLabel = permittedSchools[0]?.organizationName ?? permittedSchools[0]?.locationName ?? null
    return (
      <div className="flex flex-col gap-6">
        {returnTo ? <ReturnToOrderBar returnTo={returnTo} /> : null}
        <SchoolBalanceSection schoolLocationId={billingAccountSchoolId} schoolLabel={orgLabel} />
        <OrderReconciliationCard permittedSchools={permittedSchools} />
      </div>
    )
  }

  const showSchoolColumn = isOrgLevel && activeSchoolLocationId === null
  const totalOutstanding = totalOutstandingCents(aging)
  const heading = invoiceHeadingScope({
    rows: rowsForAging,
    scopeLabel:
      (activeSchoolLocationId !== null
        ? permittedSchools.find((s) => s.locationId === activeSchoolLocationId)?.locationName
        : isOrgLevel
          ? permittedSchools[0]?.organizationName
          : permittedSchools[0]?.locationName) ?? null,
  })

  return (
    <div className="flex flex-col gap-6">
      {returnTo ? <ReturnToOrderBar returnTo={returnTo} /> : null}
      {isOrgLevel ? (
        <SchoolFilterChips
          permittedSchools={permittedSchools}
          activeSchoolLocationId={activeSchoolLocationId}
          onSelect={(id) => {
            setActiveSchoolLocationId(id)
            setActiveBucket(null)
          }}
        />
      ) : null}

      <ArAgingCards aging={aging} activeBucket={activeBucket} onBucketSelect={setActiveBucket} />

      <Card>
        <CardHeader className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-lg font-semibold tracking-tight">Invoices · {heading.label ?? 'AR'}</h3>
            <p className="text-xs text-muted-foreground tabular-nums">
              {formatCents(totalOutstanding, aging.currency)} outstanding
            </p>
          </div>
          {heading.districtNote ? <p className="text-xs text-muted-foreground">{heading.districtNote}</p> : null}
          {activeBucket ? (
            <p className="text-xs text-muted-foreground">Filtered by aging bucket. Click the active card to clear.</p>
          ) : null}
        </CardHeader>
        <CardContent>
          <InstitutionalInvoiceTable
            rows={visibleRows}
            showSchoolColumn={showSchoolColumn}
            onRowClick={(row) => setOpenInvoice(row)}
            onDownloadSummaryPdf={(row) => void downloadSummaryPdf(row)}
            onExportOrdersCsv={(row) => void downloadManifestCsv(row)}
          />
        </CardContent>
      </Card>

      <OrderReconciliationCard permittedSchools={permittedSchools} />

      <InstitutionalInvoiceDrawer
        open={openInvoice !== null}
        invoiceId={openInvoice?.id ?? null}
        preloadRow={openInvoice}
        onDownloadSummaryPdf={(row) => void downloadSummaryPdf(row)}
        onDownloadManifestCsv={(row) => void downloadManifestCsv(row)}
        onOpenChange={(next) => {
          if (!next) {
            setOpenInvoice(null)
          }
        }}
      />
    </div>
  )
}

function NoPermittedSchoolsState() {
  return (
    <Card className="p-8">
      <h2 className="text-lg font-medium">No School AR yet</h2>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        You're not a SCHOOL_ADMIN on any campus, so there's no organization-side AR to show. If this is wrong, check
        your membership with the org owner.
      </p>
    </Card>
  )
}
